/**
 * Shake, the in-app bug reporter.
 *
 * What Shake captures is decided per environment in `src/config/env.ts` and applied here
 * verbatim: this module never asks which environment it is in, it applies what it is given.
 * Development configures no Shake app at all, so the SDK's chunk is never fetched there.
 *
 * The SDK is loaded with a dynamic import so that it, and the stylesheet it imports, stay
 * out of the entry chunk. A bug reporter is never on the critical path of the app it
 * reports on.
 */
import type { default as ShakeSdk, NetworkRequest } from '@shakebugs/browser';

import { appConfig, appEnv, type AppConfig, type AppEnvironment } from '@/config/env';
import { isAutomatedBrowser } from '@/lib/automation';

/**
 * Headers that carry a credential and never leave the browser.
 *
 * The admin authenticates with a cookie session (ADR 0016), so `cookie` is the one that
 * actually matters here: that header is the session. The other two are belt and braces.
 */
const CREDENTIAL_HEADERS = ['authorization', 'cookie', 'set-cookie'];

/**
 * Paths whose bodies are never recorded, in any environment.
 *
 * `/api/mailbox` serves set-password links (ADR 0030) and `/api/auth` carries the session,
 * so a report filed from one of those screens would otherwise attach working credentials.
 * That is true of the anonymised environments too, which is why this does not depend on
 * `networkPayloads`.
 */
const NEVER_RECORDED = ['/api/mailbox', '/api/auth'];

const REDACTED = '[redacted]';

/**
 * A url with its query string and fragment cut off.
 *
 * The admin puts free text in the query string: `/api/users?q=` carries whatever an admin
 * typed into a search box, which in this admin is routinely a customer's name or email. The
 * SDK's own redaction never touches a url, only headers and bodies, so dropping payloads
 * without dropping this would leave the contents in by another door.
 */
function pathOnly(url: string): string {
    const cut = url.search(/[?#]/);
    return cut === -1 ? url : url.slice(0, cut);
}

function redactHeaders(headers: Record<string, string> | undefined): void {
    if (!headers) return;
    for (const name of Object.keys(headers)) {
        if (CREDENTIAL_HEADERS.includes(name.toLowerCase())) headers[name] = REDACTED;
    }
}

function blankBodies(request: NetworkRequest): void {
    request.request_body = '';
    request.response_body = '';
}

/**
 * The filter for environments that record payloads. Keeps bodies, except on the paths that
 * carry credentials, and always redacts the headers that do.
 *
 * Exported for tests; app code hands it to the SDK rather than calling it.
 */
export function redactCredentials(request: NetworkRequest): NetworkRequest {
    redactHeaders(request.request_headers);
    redactHeaders(request.response_headers);
    if (NEVER_RECORDED.some((path) => request.url.includes(path))) blankBodies(request);
    return request;
}

/**
 * The filter for production. Keeps the shape of a call, path, method, status and duration,
 * and none of its contents: a response body here is a list of real customers, and a query
 * string is whatever someone typed to find one.
 *
 * Exported for tests; app code hands it to the SDK rather than calling it.
 */
export function stripPayloads(request: NetworkRequest): NetworkRequest {
    redactHeaders(request.request_headers);
    redactHeaders(request.response_headers);
    blankBodies(request);
    request.url = pathOnly(request.url);
    return request;
}

/**
 * The started SDK, or null while Shake is not running.
 *
 * Shake's own floating button is switched off (it was a tall pill fixed to the right edge at
 * z-index 2147483647, over the admin's own controls, ALS-329). The admin draws its own small
 * round button instead, `components/FeedbackButton`, which reads this to know whether there is
 * a Shake to open and calls `openShake()` when pressed.
 */
let started: typeof ShakeSdk | null = null;
const listeners = new Set<() => void>();

function setStarted(sdk: typeof ShakeSdk | null): void {
    started = sdk;
    for (const listener of listeners) listener();
}

/** Whether Shake is running here, for `useSyncExternalStore`. */
export function isShakeReady(): boolean {
    return started !== null;
}

/** Calls `listener` when Shake starts, for `useSyncExternalStore`. Returns the unsubscribe. */
export function subscribeShake(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/**
 * Shake's area picker and report form are iframes the SDK appends to <body>. While a drawer
 * or dialog is open, Radix sets `pointer-events: none` on <body> so that only the overlay
 * takes the pointer, and both iframes inherit it: a drag on the picker falls through to the
 * drawer's backdrop and closes the drawer, and the form can't be clicked at all (ALS-458).
 *
 * An id rule outranks inheritance and nothing else: where no overlay is open the iframes
 * take the pointer anyway, and an inline style the SDK sets on them still wins. The ids are
 * those of @shakebugs/browser 3.3.3.
 */
const SHAKE_FRAMES = '#shake-sdk_screenshot-iframe, #shake-sdk-ui';
const SHAKE_FRAMES_STYLE_ID = 'shake-frames-take-the-pointer';
const SHAKE_FRAMES_RULE = `${SHAKE_FRAMES} { pointer-events: auto; }`;

function letShakeFramesTakeThePointer(): void {
    if (document.getElementById(SHAKE_FRAMES_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = SHAKE_FRAMES_STYLE_ID;
    style.textContent = SHAKE_FRAMES_RULE;
    document.head.append(style);
}

/**
 * While Shake's area picker is up, focus stays in the drawer the report was opened from, so
 * Escape goes to the drawer: it asks to discard what was typed, or closes, and the picker
 * stays over it (ALS-458). Radix listens for Escape on `document` in the capture phase, so
 * this listens on `window`, which comes first, and keeps Escape from the page for as long
 * as one of Shake's frames is there. Reading the frames rather than a flag of our own means
 * nothing stays blocked if a report closes some way that doesn't tell us.
 *
 * The picker hears Escape only from inside its own frame, and the drawer keeps focus out of it,
 * so the Escape kept from the page is handed to the picker: it cancels as it does where no
 * drawer is open. The form is left alone; it closes on its own X.
 */
function keepEscapeFromThePage(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !document.querySelector(SHAKE_FRAMES)) return;
    event.stopPropagation();
    handEscapeToThePicker();
}

function handEscapeToThePicker(): void {
    const frame = document.getElementById(PICKER_IFRAME) as HTMLIFrameElement | null;
    const view = frame?.contentWindow as (Window & typeof globalThis) | null | undefined;
    const inside = frame?.contentDocument;
    if (!view || !inside) return;
    const escape = { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true, cancelable: true };
    inside.dispatchEvent(new view.KeyboardEvent('keydown', escape));
}

/**
 * What had focus inside a drawer or dialog when a report opened. Shake leaves focus on
 * <body> when it closes, outside the overlay the report was filed from. Focus outside an
 * overlay is left where Shake leaves it, as before: it would be the feedback button itself,
 * and focusing that opens its tooltip.
 */
const OVERLAY = '[role="dialog"], [role="alertdialog"]';
let returnFocusTo: HTMLElement | null = null;

function giveFocusBack(): void {
    const target = returnFocusTo;
    returnFocusTo = null;
    if (target?.isConnected) target.focus();
}

/**
 * A phone, by the admin's own measure: a touch screen narrower than `lg`, where the phone's top
 * bar replaces the sidebar (ALS-461). A tablet held sideways and every desk keep the picker.
 */
const PHONE = '(pointer: coarse) and (max-width: 1023.98px)';

/**
 * Shake opens a report on its area picker: drag a box over the page, or tap it for the whole
 * screen (the SDK takes a box of 10px or less as none). Dragging a box with a finger over a page
 * is fiddly, so on a phone the report starts from the whole screen. On Android the tap that
 * opened Shake often reached the picker as well and did this already; iOS Safari kept the picker
 * up. Tapping it ourselves, as soon as it is there, makes both the same. On a desk the picker
 * stays, so an area can still be chosen.
 *
 * The picker is a div with class `shake-sdk-screen-capture` inside the iframe whose id is in
 * SHAKE_FRAMES, both those of @shakebugs/browser 3.3.3. It goes in a frame or two after `show()`,
 * so this looks for it once a frame for half a second, and gives up quietly: an untouched picker
 * still works by hand.
 */
const PICKER_IFRAME = 'shake-sdk_screenshot-iframe';
const PICKER = '.shake-sdk-screen-capture';
const PICKER_WAIT_MS = 500;

function tapThePickerOnAPhone(): void {
    if (!window.matchMedia?.(PHONE).matches) return;
    const giveUpAt = performance.now() + PICKER_WAIT_MS;
    const look = () => {
        const frame = document.getElementById(PICKER_IFRAME) as HTMLIFrameElement | null;
        const view = frame?.contentWindow;
        const picker = frame?.contentDocument?.querySelector(PICKER);
        if (view && picker) {
            const at = { bubbles: true, cancelable: true, clientX: 1, clientY: 1 };
            const Pointer =
                (view as typeof window).PointerEvent ?? (view as typeof window).MouseEvent;
            picker.dispatchEvent(new Pointer('pointerdown', at));
            picker.dispatchEvent(new Pointer('pointerup', at));
            return;
        }
        if (performance.now() < giveUpAt) requestAnimationFrame(look);
    };
    requestAnimationFrame(look);
}

/**
 * Shake draws its screenshot with html2canvas, from a copy of the page. In iOS Safari an open
 * drawer's slide-in starts again in that copy, so a report filed from a drawer showed it barely
 * in: a strip at the right edge over the page (ALS-458). While a report is open, Radix's overlays,
 * everything carrying data-state (drawers, dialogs, their backdrops), are held at the end of
 * their animation, which is where they already are on screen. Shake's own frames are documents
 * of their own and keep moving.
 *
 * The rule comes off when the report closes. If Shake never puts a frame up, it comes off after
 * a second, so the admin's animations can't stay stopped.
 */
const HOLD_STILL_STYLE_ID = 'shake-holds-overlays-still';
const HOLD_STILL_RULE = '[data-state] { animation: none !important; }';
const FRAME_WAIT_MS = 1000;

function holdOverlaysStill(): void {
    if (document.getElementById(HOLD_STILL_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = HOLD_STILL_STYLE_ID;
    style.textContent = HOLD_STILL_RULE;
    document.head.append(style);
    setTimeout(() => {
        if (!document.querySelector(SHAKE_FRAMES)) letOverlaysMove();
    }, FRAME_WAIT_MS);
}

function letOverlaysMove(): void {
    document.getElementById(HOLD_STILL_STYLE_ID)?.remove();
}

/** What the admin puts back when a report closes, however it closes. */
function reportClosed(): void {
    letOverlaysMove();
    giveFocusBack();
}

/** Opens a Shake report, as the SDK's own button did. Does nothing while Shake is not running. */
export function openShake(): void {
    if (!started) return;
    const active = document.activeElement;
    returnFocusTo = active instanceof HTMLElement && active.closest(OVERLAY) ? active : null;
    holdOverlaysStill();
    void started.show();
    tapThePickerOnAPhone();
}

/** Forgets a started Shake. Exported for tests only. */
export function resetShakeForTests(): void {
    setStarted(null);
    returnFocusTo = null;
    document.getElementById(SHAKE_FRAMES_STYLE_ID)?.remove();
    letOverlaysMove();
    window.removeEventListener('keydown', keepEscapeFromThePage, true);
}

/**
 * Starts Shake, if this environment has a Shake app to report to.
 *
 * The config and environment are parameters with defaults, the same idiom
 * `detectEnvironment` uses: app code calls `startShake()`, tests pass their own.
 */
export async function startShake(
    config: AppConfig = appConfig,
    environment: AppEnvironment = appEnv,
): Promise<void> {
    const settings = config.shake;
    if (!settings) return;

    // The end-to-end suite drives preview and staging, where Shake is live. Not starting it
    // there also keeps the feedback button out of the suite's way, since that button only
    // shows once Shake has started, and a robot's session is noise in the inbox either way.
    // Same reasoning as the devtools panels in devChrome.ts.
    if (isAutomatedBrowser()) return;

    try {
        const { default: Shake } = await import('@shakebugs/browser');

        // Applied before start, deliberately. If any of this throws, the catch below means
        // Shake never runs at all. Applied after start, the same mistake would leave it
        // running in production with session replay on and bodies unfiltered, which is the
        // one outcome that has to be impossible: a missing bug reporter is an
        // inconvenience, customer data in a third party's storage is an incident.
        Shake.report.isSessionReplayEnabled = settings.sessionReplay;
        Shake.config.networkRequestsFilter = settings.networkPayloads
            ? redactCredentials
            : stripPayloads;
        Shake.setMetadata('environment', environment);
        // The admin draws its own button (components/FeedbackButton), so the SDK's stays off.
        Shake.config.floatingButtonEnabled = false;
        // Documented as the browser extension's, but the SDK calls it whenever a report
        // closes: cancelled, closed or sent.
        Shake.setOnShakeCloseListener(reportClosed);
        letShakeFramesTakeThePointer();
        // The same function twice is one listener, however often Shake is started.
        window.addEventListener('keydown', keepEscapeFromThePage, true);

        await Shake.start(settings.apiKey);
        setStarted(Shake);
    } catch (error) {
        // A bug reporter that cannot start is a missing convenience, not a broken admin.
        console.error('Shake did not start; in-app bug reporting is unavailable', error);
    }
}
